import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiSearch } from 'react-icons/fi';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import BrandLogoImage from '../components/BrandLogoImage';
import './BrandsPage.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';

export default function BrandsPage() {
  const [brands, setBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const loadBrands = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/brands`, { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error(`Brand request failed: ${response.status}`);
        const data = await response.json();
        setBrands(Array.isArray(data) ? data : []);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          console.error('Unable to load brands:', requestError);
          setError('We could not load the manufacturer directory. Please try again.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    loadBrands();
    return () => controller.abort();
  }, []);

  const filteredBrands = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return brands;
    return brands.filter((brand) => `${brand.brand_name || ''} ${brand.brand_description || ''}`.toLowerCase().includes(query));
  }, [brands, searchTerm]);

  return (
    <div className="brands-page">
      <Header />
      <main>
        <section className="brands-page-hero">
          <div className="brands-page-container">
            <span className="brands-page-eyebrow">Global manufacturer network</span>
            <h1>Industrial brands you can build around.</h1>
            <p>Explore trusted automation, electrical, pneumatic and industrial manufacturers available through Valley International Supply.</p>
            <div className="brands-page-search">
              <FiSearch aria-hidden="true" />
              <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search manufacturers" aria-label="Search manufacturers" />
              {!loading && <span>{filteredBrands.length} brands</span>}
            </div>
          </div>
        </section>

        <section className="brands-page-directory">
          <div className="brands-page-container">
            {loading ? (
              <div className="brands-page-loading" aria-label="Loading brands"><span /><p>Loading manufacturer directory...</p></div>
            ) : error ? (
              <div className="brands-page-state"><FiAlertCircle /><h2>Manufacturers unavailable</h2><p>{error}</p></div>
            ) : filteredBrands.length ? (
              <div className="brands-page-grid">
                {filteredBrands.map((brand) => {
                  return (
                    <Link to={`/brands/${brand.idbrand}`} key={brand.idbrand || brand.brand_name} className="brands-page-card">
                      <div className="brands-page-logo-wrap">
                        <span>{String(brand.brand_name || 'Brand').slice(0, 2).toUpperCase()}</span>
                        <BrandLogoImage brand={brand} className="brands-page-logo" />
                      </div>
                      <div className="brands-page-card-body">
                        <h2>{brand.brand_name}</h2>
                        <p>{brand.brand_description || 'Explore products and solutions from this manufacturer.'}</p>
                        <span>View products <FiArrowRight /></span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="brands-page-state"><FiSearch /><h2>No manufacturers found</h2><p>Try a different name or keyword.</p></div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
